import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { HealthWeighting } from '../../../data/ServerApiClient'

// "How this score is weighted for {company}" — the visible face of the AI differentiator, now an
// EDITOR. Shows the five category weight bars first (AI dot = the AI's weight, draggable thumb = the
// current/user weight), then the AI rationale. The user can slide the weights and Save — but only when
// they total 100%. Saving persists a user override (source=user_confirmed) that drives the score;
// "Reset to AI" snaps the sliders back to the AI weights.

const CATEGORY_ORDER = ['liquidity', 'leverage', 'efficiency', 'profitability', 'coverage'] as const
const CATEGORY_LABELS: Record<string, string> = {
  liquidity: 'Liquidity',
  leverage: 'Leverage',
  efficiency: 'Efficiency',
  profitability: 'Profitability',
  coverage: 'Coverage',
}

const pct = (w: number | undefined) => Math.round((w ?? 0) * 100)

export type CfoHealthWeightingBadgeProps = {
  weighting: HealthWeighting
  /** Persist a user override — weights as fractions keyed by category, summing to 1. */
  onSaveWeights?: (weights: Record<string, number>) => Promise<void> | void
  busy?: boolean
}

export function CfoHealthWeightingBadge({ weighting, onSaveWeights, busy }: CfoHealthWeightingBadgeProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    if (!open) return
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [open])

  const provisional = weighting.provisional
  const userOverridden = weighting.source === 'user'
  // AI weights (the dot): the snapshot when overridden, else the effective weights ARE the AI's.
  const aiPct = useMemo(() => {
    const src = weighting.ai_weights ?? weighting.weights
    const m: Record<string, number> = {}
    for (const k of CATEGORY_ORDER) m[k] = pct(src[k])
    return m
  }, [weighting])
  const effectivePct = useMemo(() => {
    const m: Record<string, number> = {}
    for (const k of CATEGORY_ORDER) m[k] = pct(weighting.weights[k])
    return m
  }, [weighting])

  const [draft, setDraft] = useState<Record<string, number>>(effectivePct)
  const total = CATEGORY_ORDER.reduce((s, k) => s + (draft[k] ?? 0), 0)
  const changed = CATEGORY_ORDER.some((k) => (draft[k] ?? 0) !== (effectivePct[k] ?? 0))
  const canSave = total === 100 && changed && !busy

  const openEditor = () => {
    setDraft(effectivePct)
    setOpen(true)
  }
  const save = async () => {
    if (!onSaveWeights || total !== 100) return
    const weights: Record<string, number> = {}
    for (const k of CATEGORY_ORDER) weights[k] = (draft[k] ?? 0) / 100
    await onSaveWeights(weights)
    setOpen(false)
  }

  return (
    <span ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openEditor())}
        aria-expanded={open}
        title="How this company's health score is weighted — click to see why and adjust"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          padding: '4px 10px',
          borderRadius: 999,
          border: '1px solid var(--hair-strong, #d4d8de)',
          background: 'var(--surface, #fff)',
          color: provisional ? 'var(--check-acc, #b7791f)' : 'var(--brand, #1b4965)',
          cursor: 'pointer',
        }}
      >
        <span aria-hidden="true">✦</span>
        {provisional ? 'Provisional weighting' : userOverridden ? 'Weighting · edited' : 'How this is weighted'}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Adjust the health score weighting"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 30,
            width: 360,
            padding: 16,
            borderRadius: 12,
            border: '1px solid var(--hair, #e3e6ea)',
            background: 'var(--surface, #fff)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand, #1b4965)', marginBottom: 10 }}>
            {provisional ? 'Provisional weighting' : 'Weighting for this company'}
          </div>

          {/* Weights FIRST — sliders with the AI dot marked under each. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
            {CATEGORY_ORDER.map((k) => {
              const val = draft[k] ?? 0
              const ai = aiPct[k] ?? 0
              return (
                <div key={k} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 38px', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--ink-2, #4c5360)' }}>{CATEGORY_LABELS[k]}</span>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={val}
                      disabled={busy}
                      onChange={(e) => setDraft((d) => ({ ...d, [k]: Number(e.target.value) }))}
                      style={{ width: '100%', display: 'block' }}
                      aria-label={`${CATEGORY_LABELS[k]} weight`}
                    />
                    {/* AI dot marker under the track */}
                    <span
                      title={`AI: ${ai}%`}
                      style={{
                        position: 'absolute',
                        left: `${ai}%`,
                        bottom: -3,
                        transform: 'translateX(-50%)',
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        background: 'var(--brand, #1b4965)',
                        border: '1px solid var(--surface, #fff)',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink, #1b1f24)', textAlign: 'right' }}>
                    {val}%
                  </span>
                </div>
              )
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 11.5, color: 'var(--muted, #6b7280)' }}>
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, background: 'var(--brand)', marginRight: 5, verticalAlign: 'middle' }} />
              AI weight
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: total === 100 ? 'var(--good-acc, #1c8d97)' : 'var(--neg, #c0492f)' }}>
              Total {total}%
            </span>
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <button type="button" disabled={!canSave} onClick={save} style={actionStyle('var(--brand, #1b4965)', canSave)}>
              {total === 100 ? 'Save weighting' : 'Total must be 100%'}
            </button>
            {changed ? (
              <button type="button" disabled={busy} onClick={() => setDraft(aiPct)} style={{ ...actionStyle('var(--ink-2, #3a3f46)'), marginLeft: 'auto' }}>
                Reset to AI
              </button>
            ) : null}
          </div>

          {/* Rationale AFTER the weights. */}
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-2, #4c5360)' }}>
            {provisional
              ? 'The five categories are weighted equally for now. The AI tunes this to the business after its first full analysis.'
              : weighting.rationale || 'The AI weighted the five categories for this business.'}
          </p>
        </div>
      )}
    </span>
  )
}

const actionStyle = (color: string, enabled = true): CSSProperties => ({
  fontSize: 12,
  fontWeight: 600,
  padding: '6px 12px',
  borderRadius: 8,
  border: `1px solid ${enabled ? color : 'var(--hair-strong, #d4d8de)'}`,
  background: 'var(--surface, #fff)',
  color: enabled ? color : 'var(--muted, #9aa0a8)',
  cursor: enabled ? 'pointer' : 'not-allowed',
})

export default CfoHealthWeightingBadge
